import { chakra, HStack, Tooltip } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { useAuthentication } from '~auth/useAuthentication';
import { ExternalLinkIcon, LockIcon, PendingIcon } from '~components/ui/icons';
import { ServiceAdminGlobalSearchAccessState } from '~graphql/__generated__/types';
import { goToOrganization } from '~utils/elevatedOrganization';

interface Props {
  organizationId: string;
  accessState: ServiceAdminGlobalSearchAccessState;
  onRequestAccess: () => void;
}

export function AccessStateCell({ organizationId, accessState, onRequestAccess }: Props) {
  const { t } = useTranslation();
  const { verifyUserPermissions } = useAuth();
  const {
    user: { organizationId: homeOrganizationId },
  } = useAuthentication();

  // Rows from the user's own organization need no access, so there's nothing to show or do.
  if (organizationId === homeOrganizationId) {
    return null;
  }

  switch (accessState) {
    case ServiceAdminGlobalSearchAccessState.Available:
      return (
        <StateAction
          tooltip={t('accessAvailableTooltip')}
          color="green.500"
          icon={<ExternalLinkIcon />}
          onClick={() => goToOrganization(organizationId)}
        >
          {t('accessAvailableGoToOrg')}
        </StateAction>
      );
    case ServiceAdminGlobalSearchAccessState.AwaitingApproval:
      return (
        <StateAction
          tooltip={t('awaitingAccessApprovalTooltip')}
          color="blue.300"
          icon={<PendingIcon />}
        >
          {t('awaitingAccessApproval')}
        </StateAction>
      );
    default:
      // NOT_REQUESTED, REJECTED, REVOKED or EXPIRED — a rejected, revoked, lapsed or stale request
      // can be requested again.
      if (!verifyUserPermissions([Permission.ServiceAdminOrgAccessRequestCreate])) {
        return null;
      }

      return (
        <StateAction
          tooltip={t('requestAccessTooltip')}
          color="blue.500"
          icon={<LockIcon />}
          onClick={onRequestAccess}
        >
          {t('requestAccess')}
        </StateAction>
      );
  }
}

function StateAction({
  tooltip,
  color,
  icon,
  onClick,
  children,
}: {
  tooltip: string;
  color: string;
  icon: ReactNode;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip label={tooltip} placement="bottom-start" hasArrow>
      <HStack
        as={onClick ? 'button' : 'span'}
        onClick={onClick}
        color={color}
        spacing="2"
        width="max-content"
        _hover={onClick ? { textDecoration: 'underline' } : undefined}
      >
        {icon}
        <chakra.span fontWeight="semibold">{children}</chakra.span>
      </HStack>
    </Tooltip>
  );
}
